import { gql } from '@apollo/client';

const equipmentViewItem = gql`
  fragment EquipmentViewItem on EquipmentView {
    id
    numberOfCables
    numberOfJumpers
    hasBlocks
    hasLinks
    hasNotes
    hasPackages
    hasTerms
  }
`;

export const equipmentFragments = {
  equipmentItem: gql`
    ${equipmentViewItem}
    fragment EquipmentItem on Equipment {
      id
      updatedTime
      updatedBy
      preparedTime
      preparedBy
      approvedTime
      approvedBy
      checkedTime
      checkedBy
      approvedByUser {
        id
        nameFirst
        nameLast
        userAccountPhoto {
          id
          photo
        }
      }
      checkedByUser {
        id
        nameFirst
        nameLast
        userAccountPhoto {
          id
          photo
        }
      }
      preparedByUser {
        id
        nameFirst
        nameLast
        userAccountPhoto {
          id
          photo
        }
      }
      updatedByUser {
        id
        nameFirst
        nameLast
        userAccountPhoto {
          id
          photo
        }
      }
      description
      primaryStatus
      constructionStatus
      equipmentCodeId
      equipmentCode {
        id
        tag
        isBreakerPanel
        breakerPanelType
      }
      layoutDrawing
      drawing2
      onelineDrawing
      schematicDrawing
      connectionDrawing
      vendorDrawing
      purchaseOrder
      specification
      disciplineCode
      serviceLevelId
      serviceLevel {
        id
        name
      }
      addlServiceLevelId
      addlServiceLevel {
        id
        name
      }
      safetyCode
      secondaryStatus
      unit
      tag
      revision
      facility
      equipmentType
      room
      latitude
      longitude
      elevation
      systemId
      system {
        id
        tag
      }
      startupSystemId
      startupSystem {
        id
        tag
      }
      parentEquipmentId
      parentEquipment {
        id
        tag
      }
      zoneName
      defaultWireTag
      misc1
      misc2
      misc3
      misc4
      misc5
      misc6
      misc7
      misc8
      misc9
      misc10
      misc11
      misc12
      misc13
      misc14
      misc15
      misc16
      misc17
      misc18
      misc19
      misc20
      miscDate1
      miscDate2
      cardRev
      ownerTag
      engineeringStatus
      ownerId
      updatedTime
      updatedBy
      updatedByUser {
        id
        nameFirst
        nameLast
        userAccountPhoto {
          id
          photo
        }
      }
      isRestricted
      equipmentView {
        ...EquipmentViewItem
      }
    }
  `,
};
export const CREATE_EQUIPMENT = gql`
  ${equipmentFragments.equipmentItem}
  mutation CreateEquipment($createEquipmentInput: CreateEquipmentInput!) {
    createEquipment(createEquipmentInput: $createEquipmentInput) {
      equipment {
        ...EquipmentItem
      }
      errors
    }
  }
`;

export const DELETE_EQUIPMENT = gql`
  mutation DeleteEquipment($deleteEquipmentInput: DeleteEquipmentInput!) {
    deleteEquipment(deleteEquipmentInput: $deleteEquipmentInput) {
      id
      errors
      infos
    }
  }
`;

export const UPDATE_EQUIPMENT = gql`
  ${equipmentFragments.equipmentItem}
  mutation UpdateEquipment($updateEquipmentInput: UpdateEquipmentInput!) {
    updateEquipment(updateEquipmentInput: $updateEquipmentInput) {
      equipment {
        ...EquipmentItem
      }
      errors
    }
  }
`;

export const UPDATE_EQUIPMENTS = gql`
  ${equipmentFragments.equipmentItem}
  mutation UpdateEquipments($updateEquipmentsInput: [UpdateEquipmentInput!]!) {
    updateEquipments(updateEquipmentsInput: $updateEquipmentsInput) {
      equipments {
        ...EquipmentItem
      }
      infos {
        tag
        messageKeys
      }
      errors {
        tag
        messageKeys
      }
    }
  }
`;

export const GET_EQUIPMENT_BY_ID = gql`
  ${equipmentFragments.equipmentItem}
  query EquipmentById($id: ID!) {
    equipmentById(id: $id) {
      ...EquipmentItem
    }
  }
`;

export const GET_EQUIPMENT_BY_IDS = gql`
  ${equipmentFragments.equipmentItem}
  query EquipmentByIds($ids: [ID!]!) {
    equipmentByIds(ids: $ids) {
      ...EquipmentItem
    }
  }
`;

export const GET_EQUIPMENT_CODE_BY_EQUIPMENT_IDS = gql`
  query EquipmentByIds($ids: [ID!]!) {
    equipmentByIds(ids: $ids) {
      id
      equipmentCode {
        id
        defaultGroundLocation
      }
    }
  }
`;

export const SEARCH_EQUIPMENT_CODE_BY_EQUIPMENT_TAG = gql`
  query searchEquipments($searchInput: SearchInput) {
    searchEquipments(searchInput: $searchInput) {
      items {
        id
        equipmentCode {
          id
          defaultGroundLocation
        }
      }
    }
  }
`;

export const ISSUE_EQUIPMENTS = gql`
  ${equipmentFragments.equipmentItem}
  mutation IssueEquipments($issueEquipmentsInput: [IssueEquipmentInput!]!) {
    issueEquipments(issueEquipmentsInput: $issueEquipmentsInput) {
      equipments {
        ...EquipmentItem
      }
      errors {
        tag
        messageKeys
      }
    }
  }
`;

export const feedbackEquipmentFragments = gql`
  fragment FeedbackEquipmentFields on Equipment {
    id
    tag
    constructionStatus
    installedBy
    installedTime
    inspectedBy
    inspectedTime
    ownerId
    ownerTag
    updatedTime
    updatedBy
    updatedByUser {
      id
      nameFirst
      nameLast
      nameMiddle
      userAccountPhoto {
        id
        photo
      }
    }
  }
`;

export const FEEDBACK_EQUIPMENT = gql`
  mutation FeedbackEquipment($feedbackEquipmentInput: FeedbackEquipmentInput!) {
    feedbackEquipment(feedbackEquipmentInput: $feedbackEquipmentInput) {
      equipment {
        ...FeedbackEquipmentFields
      }
      errors
    }
  }
  ${feedbackEquipmentFragments}
`;

export const SEARCH_EQUIPMENT = gql`
  query searchEquipments($searchInput: SearchInput) {
    searchEquipments(searchInput: $searchInput) {
      items {
        ...FeedbackEquipmentFields
      }
    }
  }
  ${feedbackEquipmentFragments}
`;

export const GET_EQUIPMENT_STATS = gql`
  query EquipmentsStat($equipmentStatsInput: EquipmentStatsInput) {
    equipmentStats(equipmentStatsInput: $equipmentStatsInput) {
      totalCount
      withoutCablesCount
      revisedCardCount
      onHoldCount
      preliminaryCount
      releasedCount
      constructedCount
      jobsiteCount
    }
  }
`;

export const GET_EQUIPMENT_VIEW_STATS = gql`
  ${equipmentViewItem}
  query EquipmentViewStats($id: ID!) {
    equipmentById(id: $id) {
      id
      equipmentView {
        ...EquipmentViewItem
      }
    }
  }
`;
